import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaCheckCircle, FaTimesCircle, FaHome, FaListAlt, FaDollarSign } from "react-icons/fa";

const SELF_CONTAINED_COLORS = {
  primary: "#007bff",
  secondary: "#6c757d",
  success: "#28a745",
  danger: "#dc3545",
  warning: "#ffc107",
  gold: "#d4af37",
  dark: "#343a40",
  light: "#f8f9fa",
  background: "#f4f6f9",
};

const SELF_CONTAINED_METRICS = {
  borderRadius: "8px",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
};

export default function PaymentStatus() {
  const { state } = useLocation();
  const navigate = useNavigate();

  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    if (!state) {
      navigate("/", { replace: true });
      return;
    }

    const timer = setTimeout(() => setShowContent(true), 100);
    return () => clearTimeout(timer);
  }, [state, navigate]);

  if (!state) return null;

  const { razorpayPaymentId, amount, status } = state;

  // STATUS LOGIC
  const isSuccess = status === "success";
  const isFailed = status === "failed";
  const isCancelled = status === "cancelled";

  const Icon = isSuccess ? FaCheckCircle : FaTimesCircle;
  const primaryColor = isSuccess ? SELF_CONTAINED_COLORS.success : SELF_CONTAINED_COLORS.danger;

  const title = isSuccess
    ? "Transaction Confirmed!"
    : isFailed
    ? "Payment Failed!"
    : "Payment Cancelled!";

  const message = isSuccess
    ? "Thank you for registering. Your payment has been processed successfully."
    : isFailed
    ? "The payment failed during verification. Please try again."
    : "The payment was cancelled by the user.";

  const cardStyle = {
    maxWidth: "450px",
    margin: "50px auto",
    padding: "30px",
    textAlign: "center",
    borderRadius: SELF_CONTAINED_METRICS.borderRadius,
    boxShadow: SELF_CONTAINED_METRICS.boxShadow,
    backgroundColor: SELF_CONTAINED_COLORS.light,
    border: `2px solid ${primaryColor}`,
    transform: showContent ? "scale(1)" : "scale(0.95)",
    opacity: showContent ? 1 : 0,
    transition: "0.4s",
  };

  const iconStyle = {
    fontSize: "3.5rem",
    marginBottom: "20px",
    color: primaryColor,
  };

  return (
    <div
      className="container py-5"
      style={{ minHeight: "100vh", backgroundColor: SELF_CONTAINED_COLORS.background }}
    >
      <div style={cardStyle}>
        <Icon style={iconStyle} />

        <h3 className="mb-2" style={{ color: primaryColor, fontWeight: "700" }}>
          {title}
        </h3>

        <p className="text-muted mb-4">{message}</p>

        {/* Amount + ID Block (hide if cancelled) */}
        {!isCancelled && (
          <div
            className="text-start p-3 mb-4"
            style={{
              backgroundColor: SELF_CONTAINED_COLORS.light,
              borderRadius: SELF_CONTAINED_METRICS.borderRadius,
              border: `1px dashed ${SELF_CONTAINED_COLORS.warning}`,
            }}
          >
            <div className="d-flex justify-content-between mb-2">
              <span className="fw-bold">
                <FaDollarSign className="me-1" style={{ color: SELF_CONTAINED_COLORS.gold }} />
                Amount Paid:
              </span>
              <span className="fw-bold" style={{ color: primaryColor }}>
                ₹{amount}
              </span>
            </div>

            <div className="d-flex justify-content-between">
              <span>Payment Reference ID:</span>
              <code>{razorpayPaymentId}</code>
            </div>
          </div>
        )}

        {/* Buttons */}
        <div className="d-flex gap-2">
          <button
            className="btn w-50"
            style={{
              backgroundColor: SELF_CONTAINED_COLORS.secondary,
              color: "white",
              borderRadius: SELF_CONTAINED_METRICS.borderRadius,
            }}
            onClick={() => navigate("/")}
          >
            <FaHome className="me-1" /> Home
          </button>

          <button
            className="btn w-50 fw-bold gradient-btn"
            onClick={() => navigate("/transactions")}
          >
            <FaListAlt className="me-1" /> View Entries
          </button>
        </div>
      </div>

      <style>{`
        .gradient-btn {
          background: linear-gradient(90deg, ${SELF_CONTAINED_COLORS.gold}, #ff4500);
          color: white;
        }
      `}</style>
    </div>
  );
}
